// Import custom IssueLabelToken until https://github.com/github/primer/issues/1142 is fixed
import {LabelToken} from '@github-ui/label-token'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {Link, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import type {Label$key} from './__generated__/Label.graphql'
import styles from './Label.module.css'

type Props = {
  label: Label$key
  hidden?: boolean
  /**
   * Href getter for the label link
   * @param name Label name
   * @returns href
   */
  getLabelHref: (name: string) => string
  fontWeight?: number
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const labelQuery = graphql`
  fragment Label on Label {
    id
    nameHTML
    color
    name
    description
  }
`

export function Label({label, hidden = false, getLabelHref, fontWeight}: Props) {
  const {nameHTML, color, id, name, description} = useFragment(labelQuery, label)

  return (
    <Tooltip
      text={description ?? ''}
      type="description"
      className={clsx(styles.LabelTooltip, !description && styles.tooltipHidden)}
    >
      {/*
        Remove link from layout and accessibility tree if hidden;
        display:none does not work because of label truncation calculations
      */}
      <Link
        href={getLabelHref(name)}
        className={clsx({'sr-only': hidden}, hidden && styles.linkHidden)}
        aria-describedby={`${id}-tooltip`}
      >
        <LabelToken
          interactive
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
          text={<VerifiedHTMLText html={nameHTML as SafeHTMLString} />}
          fillColor={`#${color}`}
          key={id}
          fontWeight={fontWeight}
        />
        <span className="sr-only" id={`${id}-tooltip`}>
          {description ?? ''}
        </span>
      </Link>
    </Tooltip>
  )
}
